import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, MessageCircle, ChevronDown } from "lucide-react";
import { usePrefersReducedMotion, useInView, Counter } from "../components/journey/hooks";
import { WaterfallChart } from "../components/journey/Charts";
import LeaseOwnChart from "../components/journey/LeaseOwnChart";
import { WATTZAP_OS_START_URL, buildWattzapOSUrl, openEnergyScanPortal } from "../lib/wattzapOS";
import { useT } from "../i18n/LanguageContext";

const WA_LINK = "https://wa.me/918639602606";

function Kicker({ children, color = "#00D4FF" }) {
  return (
    <div className="text-xs font-medium tracking-[0.2em] uppercase mb-3" style={{ color }}>
      {children}
    </div>
  );
}

/* ---------- Spine (scroll-drawn energy line) ---------- */
function Spine({ containerRef, reduced }) {
  const pathRef = useRef(null);
  const [h, setH] = useState(0);
  useEffect(() => {
    const measure = () => setH(containerRef.current?.scrollHeight || 0);
    measure();
    window.addEventListener("resize", measure);
    const timer = setTimeout(measure, 500);
    return () => { window.removeEventListener("resize", measure); clearTimeout(timer); };
  }, [containerRef]);
  useEffect(() => {
    if (reduced) {
      if (pathRef.current) pathRef.current.style.strokeDashoffset = 0;
      return;
    }
    const onScroll = () => {
      const el = containerRef.current;
      if (!el || !pathRef.current) return;
      const rect = el.getBoundingClientRect();
      const total = Math.max(1, rect.height - window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / total));
      pathRef.current.style.strokeDashoffset = 1000 * (1 - progress);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [containerRef, reduced, h]);
  return (
    <div className="absolute top-0 left-4 lg:left-10 h-full w-[6px] pointer-events-none z-[5]" aria-hidden="true">
      {h > 0 && (
        <svg width="6" height={h}>
          <defs>
            <linearGradient id="wzSpineGrad" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={h}>
              <stop offset="0" stopColor="#00D4FF" />
              <stop offset="1" stopColor="#21F59B" />
            </linearGradient>
          </defs>
          <path
            ref={pathRef}
            d={`M3 0 V ${h}`}
            pathLength="1000"
            stroke="url(#wzSpineGrad)"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
            style={{ strokeDasharray: 1000, strokeDashoffset: reduced ? 0 : 1000 }}
          />
        </svg>
      )}
    </div>
  );
}

/* ---------- Chapter 1 ---------- */
function Chapter1({ reduced }) {
  const t = useT();
  const [ref, inView] = useInView();
  const show = inView || reduced;
  return (
    <section ref={ref} data-testid="journey-ch1" className="min-h-screen flex items-center bg-[#071A2D] text-white py-24">
      <div className="max-w-5xl mx-auto px-8 lg:px-16 w-full">
        <p className="text-sm text-white/60 mb-8 max-w-xl">
          {t("A 100 kWp rice mill in Khammam, Telangana — based on a WattZap site analysis (name withheld); figures indicative.")}
        </p>
        <Kicker>{t("Chapter 1")}</Kicker>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl">{t("Where every mill starts.")}</h1>
        <div className="mt-10 font-heading text-5xl sm:text-6xl lg:text-7xl" data-testid="journey-ch1-bill">
          <Counter to={28.4} decimals={1} prefix="₹" start={inView} reduced={reduced} />
          <span className="text-2xl sm:text-3xl text-white/70">{t(" lakh/year")}</span>
        </div>
        <div className="mt-4 text-[#FFC857] font-medium text-lg" data-testid="journey-ch1-substats">
          ~₹<Counter to={11} decimals={0} start={inView} reduced={reduced} />/unit · ~
          <Counter to={2.58} decimals={2} start={inView} reduced={reduced} /> lakh units/year
        </div>
        <div className="mt-12 max-w-2xl">
          <div className="h-10 rounded-lg bg-white/10 overflow-hidden flex">
            <div className="h-full" style={{ width: "70%" }} />
            <div
              className="h-full bg-red-400/30"
              style={{
                width: "30%",
                opacity: show ? 1 : 0,
                transition: reduced ? "none" : "opacity 1.2s ease 1.4s",
              }}
            />
          </div>
          <div className="mt-2 text-xs text-white/40">{t("the annual bill — something is hiding in the right end of it")}</div>
        </div>
        <p className="mt-10 text-white/75 text-base max-w-xl leading-relaxed">
          {t("A busy rice mill in Khammam. Motors running through the day. The bill arrives, it gets paid. Nobody asks what's inside it.")}
        </p>
      </div>
    </section>
  );
}

/* ---------- Chapter 2 ---------- */
function Chapter2({ reduced }) {
  const t = useT();
  const [ref, inView] = useInView();
  const show = inView || reduced;
  return (
    <section ref={ref} data-testid="journey-ch2" className="min-h-screen flex items-center bg-mist py-24">
      <div className="max-w-5xl mx-auto px-8 lg:px-16 w-full">
        <Kicker color="#0A2540">{t("Chapter 2")}</Kicker>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-graphite">{t("The audit finds the leak.")}</h2>
        <div className="mt-12 max-w-2xl">
          <div className="h-12 rounded-lg overflow-hidden flex">
            <div
              className="h-full bg-[#00D4FF] flex items-center justify-center text-[#071A2D] text-xs sm:text-sm font-semibold"
              style={{
                width: show ? "70%" : "100%",
                transition: reduced ? "none" : "width 1s cubic-bezier(.65,0,.35,1) .3s",
              }}
            >
              {t("~70% energy you actually used")}
            </div>
            <div
              className={`h-full bg-[#FFC857] flex items-center justify-center text-[#071A2D] text-xs sm:text-sm font-semibold ${show && !reduced ? "wz-pulse-once" : ""}`}
              style={{
                width: show ? "30%" : "0%",
                transition: reduced ? "none" : "width 1s cubic-bezier(.65,0,.35,1) .3s",
              }}
            >
              {t("~30% demand")}
            </div>
          </div>
          <div className="mt-3 text-sm font-medium text-graphite" data-testid="journey-ch2-demand-label">
            {t("~₹8.4 lakh/year — driven by motors starting at the same time.")}
          </div>
        </div>
        <p className="mt-10 text-slate-600 max-w-xl leading-relaxed">
          {t("The audit starts with a free bill review, then a 2–4 week load study with a data logger on your incoming supply. No cost, no obligation — just a clear picture of what the mill actually pays for, including the charges that never show up as \"energy\".")}
        </p>
        <div className="mt-8">
          <span
            data-testid="journey-ch2-chip"
            className="inline-flex items-center gap-2 border border-[#21F59B] text-[#0A2540] bg-[#21F59B]/10 px-4 py-2 rounded-full text-sm font-medium"
          >
            {t("Step 1 is always free.")}
          </span>
        </div>
      </div>
    </section>
  );
}

/* ---------- Chapter 3 ---------- */
const LEVER_LINES = [
  { amt: "₹5.2L", line: "Rooftop solar — cheaper units, generated right where they're used." },
  { amt: "₹1.1L", line: "Power factor correction — stop paying penalties for reactive power." },
  { amt: "₹2.2L", line: "Staggered motor starts — the same machines, started 90 seconds apart." },
  { amt: "₹0.9L", line: "Time-of-day shift — flexible loads moved to cheaper grid hours." },
];

function Chapter3({ reduced }) {
  const t = useT();
  const [ref, inView] = useInView();
  const show = inView || reduced;
  return (
    <section ref={ref} data-testid="journey-ch3" className="min-h-screen flex items-center bg-white py-24">
      <div className="max-w-5xl mx-auto px-8 lg:px-16 w-full">
        <Kicker color="#0A2540">{t("Chapter 3")}</Kicker>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-graphite">{t("The savings map.")}</h2>
        <p className="mt-4 text-slate-600 max-w-xl">
          {t("Four levers, one bill: from ₹28.4L/year down to ₹19.0L/year — ₹9.4L of identified savings potential.")}
        </p>
        <div className="mt-10">
          <WaterfallChart show={show} />
          <div className="mt-1 text-xs text-slate-400">{t("breakdown illustrative pending final offer")}</div>
        </div>
        <ul className="mt-8 space-y-3 max-w-2xl">
          {LEVER_LINES.map((l, i) => (
            <li
              key={l.amt}
              className="flex items-start gap-3 text-sm text-slate-700"
              style={{
                opacity: show ? 1 : 0,
                transform: show ? "none" : "translateY(8px)",
                transition: reduced ? "none" : `opacity .5s ease ${0.9 + i * 0.15}s, transform .5s ease ${0.9 + i * 0.15}s`,
              }}
            >
              <span className="shrink-0 font-heading font-semibold text-[#0A2540] w-14">{l.amt}</span>
              <span>{t(l.line)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------- Chapter 4 ---------- */
const TIMELINE = [
  { title: "Data check", sub: "Your last 12 months of bills" },
  { title: "Load study", sub: "2–4 weeks, data logger on site" },
  { title: "Final offer, you decide", sub: "Measured numbers, no pressure" },
  { title: "Build & commissioning", sub: "18–24 weeks, DISCOM approvals handled by WattZap" },
];

function Chapter4({ reduced }) {
  const t = useT();
  const [ref, inView] = useInView();
  const show = inView || reduced;
  return (
    <section ref={ref} data-testid="journey-ch4" className="min-h-screen flex items-center bg-[#071A2D] text-white py-24">
      <div className="max-w-5xl mx-auto px-8 lg:px-16 w-full">
        <Kicker color="#FFC857">{t("Chapter 4")}</Kicker>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl">{t("Built at our cost, not yours.")}</h2>
        <div className="mt-14 relative">
          <div className="hidden md:block absolute top-[13px] left-0 right-0 h-[3px] bg-white/10 rounded" />
          <div
            className="hidden md:block absolute top-[13px] left-0 h-[3px] rounded bg-wz-gradient"
            style={{
              width: show ? "100%" : "0%",
              transition: reduced ? "none" : "width 1.8s cubic-bezier(.65,0,.35,1) .2s",
            }}
          />
          <div className="grid md:grid-cols-4 gap-8 relative">
            {TIMELINE.map((t2, i) => (
              <div key={t2.title} data-testid={`journey-ch4-node-${i + 1}`} className="flex md:block items-start gap-4">
                <div
                  className="shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center"
                  style={{
                    borderColor: "#FFC857",
                    backgroundColor: show ? "#FFC857" : "transparent",
                    boxShadow: show ? "0 0 18px rgba(255,200,87,0.5)" : "none",
                    transition: reduced ? "none" : `background-color .4s ease ${0.3 + i * 0.4}s, box-shadow .4s ease ${0.3 + i * 0.4}s`,
                  }}
                >
                  <span className="text-[11px] font-bold" style={{ color: show ? "#071A2D" : "#FFC857" }}>{i + 1}</span>
                </div>
                <div className="md:mt-4">
                  <div className="font-heading text-base">{t(t2.title)}</div>
                  <div className="mt-1 text-sm text-white/60">{t(t2.sub)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-14 text-white/75 max-w-xl leading-relaxed">
          {t("You don't sign until you've seen measured numbers from your own mill.")}
        </p>
      </div>
    </section>
  );
}

/* ---------- Chapter 5 ---------- */
function Chapter5() {
  const t = useT();
  const [ref] = useInView();
  const [mode, setMode] = useState("lease");
  return (
    <section ref={ref} data-testid="journey-ch5" className="min-h-screen flex items-center bg-mist py-24">
      <div className="max-w-5xl mx-auto px-8 lg:px-16 w-full">
        <Kicker color="#0A2540">{t("Chapter 5")}</Kicker>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-graphite">{t("Two ways to go solar.")}</h2>
        <div className="mt-10 inline-flex rounded-full bg-white border border-slate-200 p-1 shadow-card-soft">
          <button
            data-testid="journey-toggle-selffund"
            onClick={() => setMode("self")}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${mode === "self" ? "bg-[#0A2540] text-white" : "text-slate-600 hover:text-graphite"}`}
          >
            {t("Self-fund")}
          </button>
          <button
            data-testid="journey-toggle-lease"
            onClick={() => setMode("lease")}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${mode === "lease" ? "bg-wz-gradient text-[#071A2D]" : "text-slate-600 hover:text-graphite"}`}
          >
            {t("WattZap lease")}
          </button>
        </div>
        <div className="mt-8 max-w-2xl">
          {mode === "self" ? (
            <div data-testid="journey-card-selffund" className="bg-white border border-slate-200 rounded-2xl p-8 shadow-card-soft">
              <div className="font-heading text-xl text-graphite">{t("Self-fund")}</div>
              <ul className="mt-5 space-y-3 text-sm text-slate-700">
                <li>• <strong>{t("₹47.6L capex today")}</strong> · {t("~4–5 yr payback")}</li>
                <li>• {t("Breakdowns, inverter replacement, DISCOM paperwork and generation risk — all yours")}</li>
                <li>• {t("Capital pulled out of paddy season")}</li>
              </ul>
            </div>
          ) : (
            <div data-testid="journey-card-lease" className="bg-white border-2 border-[#21F59B]/50 rounded-2xl p-8 shadow-card-soft">
              <div className="font-heading text-xl text-graphite">{t("WattZap lease")}</div>
              <ul className="mt-5 space-y-3 text-sm text-slate-700">
                <li>• <strong>{t("₹0 upfront")}</strong> · {t("savings from day one")}</li>
                <li>• {t("Every operating risk on WattZap — we own and operate the plant")}</li>
                <li>• {t("Savings measured monthly — verified on your bill")}</li>
                <li>• {t("Own the plant from Year 8 at a price printed in the contract")}</li>
              </ul>
              <div className="mt-5 inline-flex items-center gap-2 bg-[#FFC857]/15 border border-[#FFC857] rounded-full px-4 py-1.5 text-sm text-graphite" data-testid="journey-net-share">
                {t("Your net savings share:")} <strong>{t("≈₹6.6 lakh/year")}</strong>&nbsp;<span className="text-slate-500">{t("(illustrative)")}</span>
              </div>
            </div>
          )}
        </div>
        <p className="mt-10 max-w-2xl font-heading text-lg text-graphite" data-testid="journey-ch5-closing">
          {t("\"You are not choosing renting over owning. You are choosing owning later, without risk, over owning now, with all of it.\"")}
        </p>
      </div>
    </section>
  );
}

/* ---------- Chapter 6 ---------- */
const ASSUMPTIONS = [
  "Figures in nominal (future) rupees assuming a realistic 4% annual grid tariff escalation.",
  "Panel output declines ~0.5% per year and is already netted into every bar.",
  "Year 13 includes the ₹1 lakh buyout and a one-time inverter replacement (~₹3.5 lakh) — the original inverter is due around takeover, and we'd rather show it than surprise you.",
  "Ownership years are net of an optional O&M contract, priced before you take ownership — never after.",
  "Buyout exercisable subject to a clean payment record; transfer taxes at actuals.",
];

function AssumptionsPanel() {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-10 max-w-3xl">
      <button
        data-testid="journey-assumptions-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between bg-white/[0.04] border border-white/10 rounded-xl px-5 py-4 text-left hover:border-white/25 transition-colors"
      >
        <span className="font-heading text-base">{t("How we calculated this — nothing hidden.")}</span>
        <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul data-testid="journey-assumptions-content" className="mt-3 bg-white/[0.03] border border-white/10 rounded-xl px-6 py-5 space-y-3 text-sm text-white/70">
          {ASSUMPTIONS.map((a) => (
            <li key={a.slice(0, 20)} className="flex gap-2"><span className="text-[#FFC857]">•</span> {a}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatBlock({ value, label, color, testId, start, reduced, decimals = 0, suffix = "L" }) {
  const t = useT();
  return (
    <div data-testid={testId} className="bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-5">
      <div className="font-heading text-3xl sm:text-4xl" style={{ color }}>
        ₹<Counter to={value} decimals={decimals} start={start} reduced={reduced} />{suffix}
      </div>
      <div className="mt-1.5 text-sm text-white/65">{t(label)}</div>
    </div>
  );
}

function Chapter6({ reduced }) {
  const t = useT();
  const [ref, inView] = useInView(0.08);
  const show = inView || reduced;
  return (
    <section ref={ref} data-testid="journey-ch6" className="relative bg-[#071A2D] text-white py-24 pb-16">
      <div className="max-w-6xl mx-auto px-8 lg:px-16 w-full">
        <Kicker>{t("Chapter 6")}</Kicker>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl">{t("The 25-year journey: lease it, then own it.")}</h2>
        <p className="mt-4 text-white/70 max-w-2xl">
          {t("Years 1–12: ₹0 invested, WattZap owns and operates, you bank measured savings. At Year 12 you buy the plant for ₹1 lakh — a price printed in the contract. From Year 13, it's yours, and the annual benefit jumps 3–4×.")}
        </p>
        <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl">
          <StatBlock value={42} label="banked during the lease" color="#00D4FF" testId="journey-stat-lease" start={inView} reduced={reduced} />
          <StatBlock value={245} label="after it becomes yours" color="#21F59B" testId="journey-stat-owned" start={inView} reduced={reduced} />
          <StatBlock value={2.9} decimals={1} suffix=" Cr" label="25-year total, from ₹0 invested today" color="#FFC857" testId="journey-stat-total" start={inView} reduced={reduced} />
        </div>
        <div className="mt-12">
          <LeaseOwnChart show={show} reduced={reduced} />
        </div>
        <AssumptionsPanel />
        <p data-testid="journey-disclaimer" className="mt-6 text-xs text-white/40 max-w-2xl">
          {t("Real WattZap site audit; indicative and subject to site feasibility, DISCOM approval and definitive agreements. Your mill gets its own version of this chart after a free audit.")}
        </p>
        <div className="mt-20 text-center max-w-2xl mx-auto">
          <h3 className="font-heading text-3xl sm:text-4xl">{t("This chart gets redrawn with your numbers.")}</h3>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={buildWattzapOSUrl()}
              onClick={(e) => { e.preventDefault(); openEnergyScanPortal(); }}
              data-testid="journey-cta-audit"
              className="btn-gradient inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm"
            >
              {t("Get a free EnergyScan")} <ArrowRight size={16} />
            </a>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="journey-cta-whatsapp"
              className="inline-flex items-center gap-2 bg-[#21F59B] text-[#071A2D] font-medium px-6 py-3.5 rounded-full text-sm hover:brightness-105"
            >
              <MessageCircle size={16} /> {t("WhatsApp us")}
            </a>
          </div>
        </div>
      </div>
      {/* spine terminus: gold sun node */}
      <div
        className="absolute bottom-10 left-4 lg:left-10 z-[6]" aria-hidden="true"
        style={{ transform: "translateX(calc(-50% + 3px))" }}
      >
        <div className="relative">
          <div
            className="w-5 h-5 rounded-full bg-[#FFC857]"
            style={{
              transform: show ? "scale(1)" : "scale(0)",
              transition: reduced ? "none" : "transform .55s cubic-bezier(.34,1.56,.64,1) 2.4s",
              boxShadow: "0 0 24px rgba(255,200,87,0.6)",
            }}
          />
          {show && !reduced && (
            <div className="absolute inset-0 rounded-full border-2 border-[#FFC857] wz-sun-ping" />
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */
export default function Journey() {
  const reduced = usePrefersReducedMotion();
  const pageRef = useRef(null);
  return (
    <div ref={pageRef} className="relative" data-testid="journey-page">
      <style>{`
        .wz-pulse-once { animation: wzGoldPulse 1.1s ease-in-out 1.3s 1 both; }
        @keyframes wzGoldPulse {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.35); }
        }
        .wz-sun-ping { animation: wzSunPing 2.4s ease-out infinite; }
        @keyframes wzSunPing {
          0% { transform: scale(1); opacity: .6; }
          70% { transform: scale(2.2); opacity: 0; }
          100% { opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .wz-pulse-once, .wz-sun-ping { animation: none !important; }
        }
      `}</style>
      <Spine containerRef={pageRef} reduced={reduced} />
      <Chapter1 reduced={reduced} />
      <Chapter2 reduced={reduced} />
      <Chapter3 reduced={reduced} />
      <Chapter4 reduced={reduced} />
      <Chapter5 />
      <Chapter6 reduced={reduced} />
    </div>
  );
}
